import { useState } from 'react';
import type { EntryPatch, EntryRow } from '../lib/day';
import { advanceTotal, allSigned, needsReason, roleOptions } from '../lib/day';
import { formatHours, formatRs } from '../lib/money';
import type { CostCode } from '../lib/costcodes';
import CostCodeSelect from './CostCodeSelect';

/**
 * The timesheet on a phone: one card per worker.
 *
 * "Name at 17px, rate beneath, fields as chunky tiles two or three across, the
 * card being worked on expanded and the rest collapsed to name, earnings and
 * status. Nothing below 14px; buttons 40px tall. Finalise and the signature
 * flow must work here too."
 *
 * The same handlers as the eleven-column table — this is a different arrangement
 * of the same row, not a second implementation. Every rule (locked until On
 * site is set, trip pay greying hours and overtime, the allowance reason going
 * red) lives in one place and both layouts read it.
 */

interface Props {
  entry: EntryRow;
  sunday: boolean;
  editable: boolean;
  canAdvance: boolean;
  costCodes: CostCode[];
  busy: boolean;
  /** Why Finalise was just refused for this man, if it was. */
  refused: string | null;
  open: boolean;
  onOpen: () => void;
  onEdit: (workerId: string, patch: EntryPatch, local: Partial<EntryRow>) => void;
  onFinalise: () => void;
  onUnfinalise: () => void;
  onAddAdvance: (amountRs: number) => void;
  onSignature: () => void;
}

export default function WorkerCard({
  entry, sunday, editable, canAdvance, costCodes, busy, refused, open,
  onOpen, onEdit, onFinalise, onUnfinalise, onAddAdvance, onSignature,
}: Props) {
  const [advanceDraft, setAdvanceDraft] = useState('');

  // Approved is locked for the clerk whatever finalised says (rule 15).
  const approved = entry.status === 'approved';
  const active = entry.attendance === 'worked' && !entry.finalised && !approved;
  const byDay = entry.payBasis === 'day';
  const locked = !editable || entry.finalised || approved;
  const advances = advanceTotal(entry);
  const reasonMissing = needsReason(entry);

  return (
    <div className={`wcard${open ? ' open' : ''}${entry.attendance === null ? ' untouched' : ''}`}>
      {/* Collapsed: name, rate, earnings, status — enough to find the man. */}
      <button type="button" className="wcard-head" onClick={onOpen} aria-expanded={open}>
        <div className="wcard-who">
          <div className="wcard-name">{entry.workerName}</div>
          <div className="wcard-rate">
            {entry.roleName ?? 'No job role set'} · {formatRs(entry.dailyRateRs)}/day
          </div>
          {entry.rejected && (
            <div className="wcard-rate" style={{ color: 'var(--text-danger)' }}>
              sent back — fix this
            </div>
          )}
        </div>
        <div className="wcard-sum">
          <div className="wcard-earned">
            {entry.attendance === 'worked' ? formatRs(entry.earnedRs) : '—'}
          </div>
          {entry.queued && <div className="wcard-rate">queued</div>}
          {approved ? (
            <span className="pill p-ok">Approved</span>
          ) : entry.finalised ? (
            <span className="pill p-ok">Final</span>
          ) : entry.attendance === 'absent' ? (
            <span className="pill p-no">Absent</span>
          ) : entry.attendance === 'worked' ? (
            <span className="pill p-wait">Not final</span>
          ) : (
            <span className="pill p-info">To enter</span>
          )}
        </div>
      </button>

      {open && (
        <div className="wcard-body">
          {/* Chunky tiles, two or three across. */}
          <div className="tiles">
            <label className="tile tile-wide">
              <span className="tile-label">On site</span>
              <select
                value={entry.attendance ?? ''}
                disabled={locked}
                onChange={(e) => {
                  const value = e.target.value === '' ? null : (e.target.value as 'worked' | 'absent');
                  onEdit(
                    entry.workerId,
                    { attendance: value },
                    value === 'absent'
                      ? {
                          attendance: value, hoursTenths: 0, otTenths: 0, allowanceRs: 0,
                          sundayAllowanceRs: 0, salaryRs: 0, otRs: 0, earnedRs: 0, baseEarnedRs: 0,
                        }
                      : { attendance: value },
                  );
                }}
              >
                <option value="">— pick —</option>
                <option value="worked">Worked</option>
                <option value="absent">Absent</option>
              </select>
            </label>

            <label className="tile tile-wide">
              <span className="tile-label">Job and pay basis</span>
              <select
                value={`${entry.jobRoleId ?? ''}:${entry.payBasis}`}
                disabled={!active || locked}
                onChange={(e) => {
                  const [roleId, basis] = e.target.value.split(':');
                  const payBasis = basis === 'trip' ? 'trip' : 'day';
                  onEdit(
                    entry.workerId,
                    roleId ? { jobRoleId: roleId, payBasis } : { payBasis },
                    { payBasis, jobRoleId: roleId ?? null },
                  );
                }}
              >
                {roleOptions(entry).map((role) => (
                  <optgroup key={role.id} label={role.name}>
                    <option value={`${role.id}:day`}>{role.name} — day rate</option>
                    <option value={`${role.id}:trip`}>{role.name} — trip pay</option>
                  </optgroup>
                ))}
                {roleOptions(entry).length === 0 && <option value=":day">No job role set</option>}
              </select>
            </label>

            <label className="tile tile-full">
              <span className="tile-label">Cost code</span>
              <CostCodeSelect
                codes={costCodes}
                value={entry.costCodeId}
                usual={entry.usualCostCodeId}
                disabled={!active || locked}
                onChange={(costCodeId) => onEdit(entry.workerId, { costCodeId }, { costCodeId })}
              />
            </label>

            {/* Trip pay greys out hours and overtime, same as the table. */}
            <label className="tile">
              <span className="tile-label">Hours</span>
              <input
                type="number"
                step="0.5"
                min="0"
                max="24"
                inputMode="decimal"
                value={formatHours(entry.hoursTenths)}
                disabled={!active || !byDay || locked}
                onChange={(e) => {
                  const tenths = Math.round((Number(e.target.value) || 0) * 10);
                  onEdit(entry.workerId, { hoursTenths: tenths }, { hoursTenths: tenths });
                }}
              />
            </label>

            <label className="tile">
              <span className="tile-label">Overtime</span>
              <input
                type="number"
                step="0.5"
                min="0"
                inputMode="decimal"
                value={formatHours(entry.otTenths)}
                disabled={!active || !byDay || locked}
                onChange={(e) => {
                  const tenths = Math.round((Number(e.target.value) || 0) * 10);
                  onEdit(entry.workerId, { otTenths: tenths }, { otTenths: tenths });
                }}
              />
            </label>

            {/* On a Sunday this pairs with the Sunday box; on any other day it
                would otherwise sit alone in half a row. */}
            <label className={sunday ? 'tile' : 'tile tile-full'}>
              <span className="tile-label">Allowance</span>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                value={entry.allowanceRs || ''}
                disabled={!active || locked}
                onChange={(e) => {
                  const amount = Math.max(0, Math.trunc(Number(e.target.value) || 0));
                  onEdit(entry.workerId, { allowanceRs: amount }, { allowanceRs: amount });
                }}
              />
            </label>

            {/* Only on Sundays, as on the table — where it is the second box
                inside the allowance cell. */}
            {sunday && (
              <label className="tile">
                <span className="tile-label">Sunday allowance</span>
                <input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={entry.sundayAllowanceRs || ''}
                  disabled={!active || locked}
                  onChange={(e) => {
                    const amount = Math.max(0, Math.trunc(Number(e.target.value) || 0));
                    onEdit(entry.workerId, { sundayAllowanceRs: amount }, { sundayAllowanceRs: amount });
                  }}
                />
              </label>
            )}

            <label className="tile tile-full">
              <span className="tile-label">
                Reason for allowance
                {reasonMissing && <span style={{ color: 'var(--text-danger)' }}> — required</span>}
              </span>
              <input
                type="text"
                className={reasonMissing ? 'need' : undefined}
                data-reason={entry.workerId}
                value={entry.allowanceReason ?? ''}
                placeholder={entry.allowanceRs > 0 ? 'required' : 'why was it paid'}
                disabled={!active || locked}
                onChange={(e) =>
                  onEdit(
                    entry.workerId,
                    { allowanceReason: e.target.value },
                    { allowanceReason: e.target.value },
                  )
                }
              />
            </label>

            <label className="tile tile-full">
              <span className="tile-label">
                Advance
                {advances > 0 && ` — ${formatRs(advances)} given today`}
              </span>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                placeholder="add cash given"
                value={advanceDraft}
                disabled={!active || !canAdvance || busy}
                onChange={(e) => setAdvanceDraft(e.target.value)}
                onBlur={() => {
                  const amount = Math.trunc(Number(advanceDraft) || 0);
                  if (amount > 0) onAddAdvance(amount);
                  setAdvanceDraft('');
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              />
            </label>
          </div>

          <div className="wcard-earnline">
            <span>Earned today</span>
            <span className="wcard-earned">
              {entry.attendance === 'worked' ? formatRs(entry.earnedRs) : formatRs(0)}
            </span>
          </div>

          {/* Both tappable, as on the table, and both open the signature popup. */}
          {advances > 0 && (
            <button type="button" className="wcard-sig" onClick={onSignature}>
              {allSigned(entry) ? (
                'signature held — tap to see it'
              ) : (
                <span style={{ color: 'var(--text-danger)' }}>no signature — tap to sign</span>
              )}
            </button>
          )}

          <div className="bar" style={{ marginTop: 10 }}>
            {entry.attendance === null ? (
              <span className="sub">Mark him on site first.</span>
            ) : approved ? (
              <span className="sub">Approved by the owner.</span>
            ) : entry.finalised ? (
              editable && (
                <button type="button" className="btn" disabled={busy} onClick={onUnfinalise}>
                  Undo finalise
                </button>
              )
            ) : (
              editable && (
                <button type="button" className="btn pri" disabled={busy} onClick={onFinalise}>
                  Finalise
                </button>
              )
            )}
          </div>
          {refused && (
            <div className="wcard-rate" style={{ color: 'var(--text-danger)', marginTop: 6 }}>
              {refused}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
